import Head from "next/head"
import React, { useMemo } from "react"

import useAuth from "@hooks/useAuth"
import type { GaTracking } from "@mono/essentials/types/SettingType"
import type { IDomain } from "@mono/essentials/types/UserTypes"

import ButtonPreview from "./ButtonPreview"
import CalendarPreview from "./CalendarPreview"
import CustomWidgetPreview, { Footer, Header } from "./CustomWidgetPreview"
import DefaultWidgetPreview from "./DefaultWidgetPreview"

interface PreviewProps {
  data: any
  type: string
  isIntegratedPage?: boolean
  domainBasicDetails?: IDomain & {
    ga_tracking?: GaTracking
  }
  nextStep?: string
}

const Preview = ({
  data,
  type,
  isIntegratedPage = false,
  domainBasicDetails,
  nextStep,
}: PreviewProps) => {
  const { domainDetails } = useAuth()
  const widgtetOnIframe = window === window.parent

  const { gtm, ga } = useMemo(() => {
    if (!domainBasicDetails?.ga_tracking) return { gtm: null, ga: null }

    const gtmData = domainBasicDetails.ga_tracking.gtm
    const gaData = domainBasicDetails.ga_tracking.ga

    return {
      gtm: gtmData?.id ? gtmData : null,
      ga: gaData?.id ? gaData : null,
    }
  }, [domainBasicDetails?.ga_tracking])

  return (
    <div className={"widget-theme-wrapper"}>
      {Boolean(ga || gtm) && process.env.NODE_ENV === "production" && (
        <Head>
          {gtm?.id && gtm?.active && (
            <>
              {/* Google Tag Manager */}
              <script
                id={"gtm-script"}
                dangerouslySetInnerHTML={{
                  __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${gtm.id}')`,
                }}
              />

              {/* Google Tag Manager NoScript */}
              <noscript>
                <iframe
                  src={`https://www.googletagmanager.com/ns.html?id=${gtm.id}`}
                  height={"0"}
                  width={"0"}
                  style={{ display: "none", visibility: "hidden" }}
                  title={"Google Tag Manager"}
                />
              </noscript>
            </>
          )}

          {ga?.id && ga?.active && (
            <>
              {/* Google Analytics GA4 */}
              <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga.id}`} />
              {/* eslint-disable-next-line react/no-danger */}
              <script
                id={"ga4-script"}
                dangerouslySetInnerHTML={{
                  __html: `
                  window.dataLayer = window.dataLayer || [];
                      function gtag(){dataLayer.push(arguments);}
                      gtag('js', new Date());
                      gtag('config', '${ga.id}', {
                        linker: {
                          accept_incoming: true,
                          domains: ${JSON.stringify(
                            ga.linked_domains
                              .split(",")
                              .map((d: string) => d.trim())
                              .filter(Boolean)
                          )}
                        }
                      });
                      gtag('set', 'linker', {
                        domains: ${JSON.stringify(
                          ga.linked_domains
                            .split(",")
                            .map((d: string) => d.trim())
                            .filter(Boolean)
                        )}
                      });
                  `,
                }}
              />
            </>
          )}
        </Head>
      )}

      {type === "calendar" &&
        (widgtetOnIframe && isIntegratedPage ? (
          <div className={"lg:py-8 md:py-4 py-2 overflow-auto"}>
            <div className={"max-w-screen-lg mx-auto"}>
              <Header
                domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
                heading={"Availability for booking"}
                parentClassName={""}
                isIntegratedPage={isIntegratedPage}
              />
              <CalendarPreview
                data={data}
                showBigCalendar={
                  data.calendar_for === "all_products" &&
                  data.show_product_in_calendar === "directly_show_in_calendar"
                }
                selectedProduct={data.product}
                productView={data.show_product_detail_page}
                isIntegratedPage={isIntegratedPage}
                domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
                nextStep={nextStep}
                hideCustomerDetails
              />
              <Footer domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails} />
            </div>
          </div>
        ) : (
          <CalendarPreview
            data={data}
            showBigCalendar={
              data.calendar_for === "all_products" &&
              data.show_product_in_calendar === "directly_show_in_calendar"
            }
            selectedProduct={data.product}
            productView={data.show_product_detail_page}
            isIntegratedPage={isIntegratedPage}
            domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
            nextStep={nextStep}
          />
        ))}

      {type === "button" && (
        <ButtonPreview
          data={data}
          isIntegratedPage={isIntegratedPage}
          domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
          nextStep={nextStep}
        />
      )}

      {type === "custom_widget" && (
        <CustomWidgetPreview
          data={data}
          isIntegratedPage={isIntegratedPage}
          domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
          nextStep={nextStep}
        />
      )}

      {!type && (
        <DefaultWidgetPreview
          isIntegratedPage={isIntegratedPage}
          domainDetails={isIntegratedPage ? domainBasicDetails : domainDetails}
          nextStep={nextStep}
        />
      )}
    </div>
  )
}

export default Preview
